'use client';

import { useTranslations } from 'next-intl';
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
interface TruncateTextProps {
  text: string;
  maxLength?: number;
  className?: string;
}

export function TruncateText({
  text,
  maxLength = 2,
  className,
}: TruncateTextProps) {
  const t = useTranslations('TruncateText');
  const DEFAULT_LINE_HEIGHT = 24;

  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const [maxHeight, setMaxHeight] = useState<string | number>(
    DEFAULT_LINE_HEIGHT * maxLength
  );
  const [calculatedInitMaxHeight, setCalculatedInitMaxHeight] =
    useState<number>(0);
  const textRef = React.useRef<HTMLParagraphElement>(null);

  React.useEffect(() => {
    if (textRef.current) {
      const lineHeight = parseInt(
        getComputedStyle(textRef.current).lineHeight,
        10
      );
      setCalculatedInitMaxHeight(lineHeight * maxLength);
      setIsTruncated(textRef.current.scrollHeight > calculatedInitMaxHeight);
      setMaxHeight(calculatedInitMaxHeight);
    }
  }, [text, maxLength, calculatedInitMaxHeight]);

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };
  React.useEffect(() => {
    if (isExpanded) {
      setMaxHeight(textRef.current?.scrollHeight ?? 5000);
    } else {
      setMaxHeight(calculatedInitMaxHeight);
    }
  }, [isExpanded, calculatedInitMaxHeight]);

  return (
    <div className='relative space-y-2' onClick={handleToggle}>
      <p
        ref={textRef}
        className={cn(
          {
            'bg-linear-to-b from-[#1B1B1B] to-transparent bg-clip-text':
              isTruncated,
            'text-transparent': isTruncated && !isExpanded,
            'text-black': isExpanded,
          },
          className
        )}
        style={{
          maxHeight: isExpanded ? maxHeight : `${maxHeight}px`,
          overflow: 'hidden',
          transition: 'max-height 0.3s ease',
        }}
      >
        {text}
      </p>
      {isTruncated && !isExpanded && (
        <Button
          variant='outline'
          className='absolute -bottom-2 left-1/2 h-auto w-auto -translate-x-1/2 text-xs font-semibold'
        >
          {t('readMore')}
        </Button>
      )}
    </div>
  );
}
